import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { TYPE_LABELS, CATEGORIES, CITIES } from "@/lib/format";
import { ListingCard } from "@/components/ListingCard";
import { Input } from "@/components/ui/input";

const INTRO = {
  auction: "سجلات تجارية ومركبات معروضة للمزاد العلني. تزايد الشركات حتى انتهاء الوقت، وعمولة الموقع 1% فقط.",
  sale: "مركبات ومعدات لوجستية معروضة للبيع المباشر بسعر ثابت بدون مزاد.",
  rent: "شاحنات ومعدات ومستودعات متاحة للإيجار.",
  job: "فرص عمل في قطاع النقل والخدمات اللوجستية.",
  invest: "فرص شراكة وبيع حصص واستثمار في منشآت النقل والخدمات اللوجستية، مع بيانات مالية تظهر بعد موافقة الإدارة.",
};

const Chip = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick} className={`px-3.5 py-1.5 rounded-md text-sm font-semibold border transition-colors ${active ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}>{children}</button>
);

export default function Browse({ type }) {
  const [params] = useSearchParams();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState("");
  const [city, setCity] = useState("");

  useEffect(() => { setCat(""); setCity(""); setQ(params.get("q") || ""); }, [type, params]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/listings", { params: { type, q: q || undefined, category: cat || undefined, city: city || undefined } })
        .then((r) => setItems(r.data)).catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [type, q, cat, city]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={`browse-page-${type}`}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2" data-testid="browse-title">{TYPE_LABELS[type]}</h1>
          <p className="text-slate-500 max-w-2xl">{INTRO[type]}</p>
        </div>
        <Link to={`/add?type=${type}`} data-testid="browse-add-btn" className="flex items-center gap-1.5 bg-sun hover:bg-sun-dark text-white rounded-lg px-4 h-10 text-sm font-bold w-fit transition-colors shadow-sm"><Plus size={16} />أضف إعلان هنا</Link>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-8 space-y-4 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input data-testid="browse-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالعنوان أو الوصف أو رقم الإعلان..." className="pr-10 h-11 rounded-lg" />
          </div>
          <select data-testid="browse-city-select" value={city} onChange={(e) => setCity(e.target.value)} className="h-11 rounded-lg border border-slate-200 px-3 bg-white text-sm">
            <option value="">كل المدن</option>
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testId="cat-all" active={!cat} onClick={() => setCat("")}>الكل</Chip>
          {CATEGORIES[type].map((c) => <Chip key={c.v} testId={`cat-${c.v}`} active={cat === c.v} onClick={() => setCat(c.v)}>{c.l}</Chip>)}
        </div>
      </div>
      {items === null ? <div className="text-center py-20 text-slate-400">جارِ التحميل...</div>
        : items.length === 0 ? <div className="text-center py-20 text-slate-500" data-testid="browse-empty">لا توجد إعلانات مطابقة حالياً.</div>
        : <div className={`grid gap-5 sm:grid-cols-2 ${type === "job" ? "lg:grid-cols-3" : "lg:grid-cols-4"}`} data-testid="browse-grid">{items.map((l) => <ListingCard key={l.id} l={l} />)}</div>}
    </div>
  );
}
